/* eslint-disable jira/filename-case */

import { useCallback } from 'react';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/import-whitelist
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useResource, type RouteResource } from '@atlassian/react-resource-router';
import type { Theme } from '@jira/platform__common-constants/src/theme-preference.tsx';
import {
	THEME_PREFERENCES_ANALYTICS_SUBJECT,
	THEME_PREFERENCES_ANALYTICS_ACTION,
	DEFAULT_COLOR_MODE,
} from './themeColorModes.constants.tsx';
import type { ThemePreferenceResource } from './ThemePreferenceResource.types.tsx';
import { setThemePreference } from './ThemePreferenceServicesSetThemePreference.tsx';

/**
 * New implementation of useThemePreference that uses the shared-user-preferences feature.
 * This implementation requires tenant context (cloudId, activationId, accountId).
 */
export const useThemePreference = (
	themePreferenceResource: RouteResource<ThemePreferenceResource>,
): {
	theme: Theme;
	setTheme: (theme: Theme) => Promise<void>;
} => {
	const { data, update: updateThemePreferenceResource } = useResource(themePreferenceResource);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Get tenant context using hooks
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const accountId = useAccountId();

	const fireThemeAnalytics = useCallback(
		(current?: Theme, next?: Theme) => {
			const OStheme =
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window?.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
					? 'dark'
					: 'light';
			const analyticsEvent = createAnalyticsEvent({
				action: THEME_PREFERENCES_ANALYTICS_ACTION,
				actionSubject: THEME_PREFERENCES_ANALYTICS_SUBJECT,
			});
			fireUIAnalytics(analyticsEvent, {
				newPreference: next,
				oldPreference: current,
				OStheme,
			});
		},
		[createAnalyticsEvent],
	);

	const setTheme = useCallback(
		async (theme: Theme): Promise<void> => {
			updateThemePreferenceResource(() => ({ theme }));
			if (data?.theme !== theme) {
				fireThemeAnalytics(data?.theme, theme);
				await setThemePreference(theme, cloudId, activationId, accountId);
			}
		},
		[
			data?.theme,
			updateThemePreferenceResource,
			fireThemeAnalytics,
			cloudId,
			activationId,
			accountId,
		],
	);

	return {
		theme: data?.theme ?? DEFAULT_COLOR_MODE,
		setTheme,
	};
};
